import React, { useEffect, useState } from "react";
import "./PlinkoGame.css";
import MatterScene from "./MatterScene";
import AdminPanel from "./AdminPanel";
import ImageGallery from "./ImageGallery";
import { useHitCounter } from "./useHitCounter";
import { byTag, PRODUCTS_BASE_PATH } from "../../products/catalog.generated";
import { isTabletWidthOnly as isTablet } from "../shared/device";

const PLINKO_PRODUCTS_BASE_PATH = PRODUCTS_BASE_PATH;

const ALL_AVAILABLE_IMAGES = byTag("plinko-pool");

const DEFAULT_IMAGE_PATHS = ALL_AVAILABLE_IMAGES.slice(0, 5);

function migrateGoalImagePath(path: string) {
  const fileName = path.split("/").pop();
  return fileName ? `${PLINKO_PRODUCTS_BASE_PATH}/${fileName}` : path;
}

interface PlinkoGameProps {
  onBackToHome: () => void;
}

const PlinkoGame: React.FC<PlinkoGameProps> = ({ onBackToHome }) => {
  const [imagePaths, setImagePaths] = useState<string[]>(DEFAULT_IMAGE_PATHS);
  const [adminMode, setAdminMode] = useState(false);
  const [selectedGoalIndex, setSelectedGoalIndex] = useState<number | null>(
    null,
  );

  const { hitCounts, incrementHit, resetHits } = useHitCounter(imagePaths);

  // Load saved images on mount
  useEffect(() => {
    const stored = localStorage.getItem("goalImages");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length === 5) {
          const migrated = parsed.map((path) =>
            typeof path === "string" ? migrateGoalImagePath(path) : path,
          );
          setImagePaths(migrated);
          return;
        }
      } catch (e) {
        console.warn("Formato inválido no localStorage para goalImages");
      }
    }

    localStorage.setItem("goalImages", JSON.stringify(DEFAULT_IMAGE_PATHS));
    setImagePaths(DEFAULT_IMAGE_PATHS);
  }, []);

  // Save imagePaths on change
  useEffect(() => {
    localStorage.setItem("goalImages", JSON.stringify(imagePaths));
  }, [imagePaths]);

  const onGoalHit = (imagePath: string) => {
    incrementHit(imagePath);
  };

  const replaceGoalImage = (newPath: string) => {
    if (selectedGoalIndex === null) return;
    const updatedPaths = [...imagePaths];
    updatedPaths[selectedGoalIndex] = newPath;
    setImagePaths(updatedPaths);
    setSelectedGoalIndex(null);
  };

  const exitAdmin = () => {
    setAdminMode(false);
    setSelectedGoalIndex(null);
  };

  const toggleAdmin = () => {
    setAdminMode((mode) => !mode);
    setSelectedGoalIndex(null);
  };

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button
          className="back-button"
          onClick={onBackToHome}
          title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de
        dispositivo no navegador.
      </div>
    );
  }

  return (
    <div className={`plinko ${adminMode ? "admin-mode" : ""}`}>
      <button
        className="back-button"
        onClick={onBackToHome}
        title="Voltar ao início">
        ← Voltar
      </button>
      <MatterScene goalImages={imagePaths} onGoalHit={onGoalHit} />

      {adminMode ? (
        <AdminPanel
          imagePaths={imagePaths}
          hitCounts={hitCounts}
          selectedGoalIndex={selectedGoalIndex}
          onSelectGoal={setSelectedGoalIndex}
          onReplaceGoalImage={replaceGoalImage}
          onResetHits={resetHits}
          onExitAdmin={exitAdmin}
          allAvailableImages={ALL_AVAILABLE_IMAGES}
        />
      ) : (
        <ImageGallery
          imagePaths={imagePaths}
          onMiddleImageClick={toggleAdmin}
        />
      )}
    </div>
  );
};

export default PlinkoGame;
